/* eslint-disable react/jsx-props-no-spreading */
import { useSpring, animated } from '@react-spring/web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDrag } from 'react-use-gesture';

import { useResizeObserver } from '../../hooks/useObserver';
import * as AppPropTypes from '../../lib/PropTypes';

import { useItemRefs } from '../../contexts/ItemRefContext';
import PillButton from '../buttons/Pill';
import Shortcut from '../cards/Shortcut';
import Arrow from '../icons/Arrow';
import SectionHeader from '../partials/SectionHeader';

import styles from '../../../styles/lists/carousel-list.module.scss';

const GESTURE_SENSITIVITY_BOUNDS = 0.1;
const GESTURE_SENSITIVITY = 0.8;
const VELOCITY_THRESHOLD = 0.3;
const DISTANCE_RATIO_THRESHOLD = 0.5;

const propTypes = {
    title: PropTypes.node,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    link: PropTypes.oneOfType([AppPropTypes.link, PropTypes.string]),
    cardComponent: PropTypes.elementType,
    containerRef: PropTypes.any, // eslint-disable-line
    cardProps: PropTypes.object /* eslint-disable-line */,
    items: PropTypes.arrayOf(PropTypes.oneOfType([AppPropTypes.film])),
    slotWidth: PropTypes.number,
    minSlots: PropTypes.number,
    className: PropTypes.string,
    slotClassName: PropTypes.string,
    cardClassName: PropTypes.string,
    isVisibleClassName: PropTypes.string, // TODO naming
    isDark: PropTypes.bool,
    withoutNav: PropTypes.bool,
    withFilmRefs: PropTypes.bool,
};

const defaultProps = {
    title: null,
    description: null,
    link: null,
    cardComponent: Shortcut,
    containerRef: null,
    cardProps: null,
    items: null,
    slotWidth: null,
    minSlots: 1,
    className: null,
    slotClassName: null,
    cardClassName: null,
    isVisibleClassName: null,
    isDark: false,
    withoutNav: false,
    withFilmRefs: false,
};

const CarouselList = ({
    title,
    description,
    link,
    cardComponent: CardComponent,
    cardProps,
    items,
    slotWidth,
    minSlots,
    className,
    slotClassName,
    cardClassName,
    isVisibleClassName,
    containerRef: providedContainerRef,
    isDark,
    withoutNav,
    withFilmRefs,
}) => {
    const intl = useIntl();
    const containerRef = useRef(null);
    const itemsRef = useRef([]);
    const { registerRef, unregisterRef, currentItem } = useItemRefs();
    // const { id: currentId } = currentItem || {};

    useEffect(() => {
        if (items !== null && withFilmRefs) {
            (items || []).forEach((it, idx) => {
                const { id = null, type = null } = it;
                if (id !== null && type === 'film') {
                    registerRef(it, { current: itemsRef.current[idx] });
                }
            });
        }
        return () => {
            (items || []).forEach((it) => {
                const { id = null, type = null } = it;
                if (id !== null && type === 'film') {
                    unregisterRef(it);
                }
            });
        };
    }, [items, registerRef, withFilmRefs]);

    // TODO: almost there... see selected
    // console.log('currentItem', currentItem);

    const {
        ref: containerResizeRef,
        entry: { contentRect: containerRect },
    } = useResizeObserver();
    const {
        ref: firstItemResizeRef,
        entry: { contentRect: firstItemRect },
    } = useResizeObserver();
    const { width: rowWidth = 0 } = containerRect || {};
    const { width: itemWidth = 0 } = firstItemRect || {};
    const slotcounts =
        slotWidth !== null && rowWidth > 0
            ? Math.max(Math.floor(rowWidth / slotWidth), minSlots)
            : null;
    const [animatedStyles, api] = useSpring(() => ({
        x: 0,
        config: { tension: 250, friction: 35 },
    }));
    const { length } = items || [];
    const [first] = items || [];

    const [currentIndex, setCurrentIndex] = useState(0);
    // Reset on list change
    useEffect(() => {
        setCurrentIndex(0);
    }, [length, first, setCurrentIndex]);

    const [currentX, setCurrentX] = useState(0);
    const [maxIndex, setMaxIndex] = useState(length);
    const [numberOfVisibleItems, setNumberOfVisibleItems] = useState(0);
    const [carouselEnabled, setCarouselEnabled] = useState(false);

    const next = useCallback(() => {
        const jump = Math.round(rowWidth / itemWidth);
        const ahead = currentIndex + jump;
        const max = length - jump;
        const index = Math.min(ahead, max);

        setMaxIndex(max);
        setCurrentIndex(index);
    }, [currentIndex, itemWidth, length, setCurrentIndex, setCurrentX]);

    const previous = useCallback(() => {
        const jump = Math.round(rowWidth / itemWidth);
        const behind = currentIndex - jump;
        const index = Math.max(behind, 0);

        setCurrentIndex(index);
    }, [currentIndex, itemWidth, setCurrentIndex, setCurrentX]);

    const onGestureEnd = (mx, velocity, direction) => {
        // gesture is not "horizontal enough"
        if (Math.abs(direction[0]) < 0.5) return api.start({ x: currentX });

        // if quick enough (velocity) or goes beyond the threshold (distance)
        if (
            ((mx < 0 && velocity > VELOCITY_THRESHOLD) ||
                mx <= itemWidth * DISTANCE_RATIO_THRESHOLD * -1) &&
            currentIndex !== maxIndex
        )
            return next();
        if (
            ((mx > 0 && velocity > VELOCITY_THRESHOLD) ||
                mx >= itemWidth * DISTANCE_RATIO_THRESHOLD) &&
            currentIndex !== 0
        )
            return previous();

        return api.start({ x: currentX });
    };

    const bind = useDrag(({ event, active, velocity, direction, movement: [mx] }) => {
        if (!carouselEnabled) return;
        if (event.pointerType !== 'touch') return;

        const sensitivity =
            (currentIndex === 0 && mx > 0) || (currentIndex === maxIndex && mx < 0)
                ? GESTURE_SENSITIVITY_BOUNDS
                : GESTURE_SENSITIVITY;

        if (!active) {
            onGestureEnd(mx, velocity, direction);
        } else {
            api.start({
                x: currentX + mx * sensitivity,
                immediate: true,
            });
        }
    });

    useEffect(() => {
        setCurrentIndex(0);
        setMaxIndex(length);
    }, [length]);

    useEffect(() => {
        const visible = Math.ceil(rowWidth / (itemWidth + 10)); // FIXME +10 here is a safety so that if the calculation is never leaving out an item
        const x = currentIndex * itemWidth * -1;

        setNumberOfVisibleItems(visible);
        setCurrentX(x);
        setCarouselEnabled(visible < length);

        api.start({ x });
    }, [
        length,
        rowWidth,
        itemWidth,
        setNumberOfVisibleItems,
        setCurrentX,
        setCarouselEnabled,
        currentIndex,
    ]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isDark]: isDark,
                    [styles.isEnabled]: carouselEnabled,
                    [styles.isReady]: slotcounts !== null,
                    [className]: className !== null,
                },
            ])}
            ref={providedContainerRef}
        >
            <div className={styles.header}>
                {title !== null ? (
                    <SectionHeader
                        className={styles.title}
                        title={title}
                        // TODO: Add lengthType to description
                        // description={`HERE!! - ${description}`}
                        description={description}
                        url={link}
                        buttonLabel={intl.formatMessage({
                            defaultMessage: 'View section',
                            description: 'Button label',
                        })}
                        isDark={isDark}
                    />
                ) : null}
            </div>
            <div className={styles.inner} {...bind()}>
                {!withoutNav ? (
                    <PillButton
                        className={classNames(styles.navButton, styles.navButtonPrevious, {
                            [styles.arrowVisible]: currentIndex !== 0,
                        })}
                        onClick={previous}
                        tabIndex={currentIndex !== 0 ? null : -1}
                        icon={<Arrow className={styles.arrow} direction="left" />}
                        isDark={isDark}
                        isContrast
                    />
                ) : null}
                {!withoutNav ? (
                    <PillButton
                        className={classNames(styles.navButton, styles.navButtonNext, {
                            [styles.arrowVisible]: currentIndex !== maxIndex,
                        })}
                        onClick={next}
                        tabIndex={currentIndex !== maxIndex ? null : -1}
                        icon={<Arrow className={styles.arrow} direction="right" />}
                        isDark={isDark}
                        isContrast
                    />
                ) : null}
                <div className={styles.heightWrap}>
                    {items !== null ? (
                        <animated.div
                            style={animatedStyles}
                            ref={(ref) => {
                                containerRef.current = ref;
                                containerResizeRef.current = ref;
                            }}
                            className={styles.row}
                        >
                            {items.map((item, index) => (
                                <div
                                    key={`${item.id}-${index + 1}`}
                                    style={{
                                        width: slotcounts !== null ? `${100 / slotcounts}%` : null,
                                    }}
                                    className={classNames(styles.slot, {
                                        [slotClassName]: slotClassName !== null,
                                        [isVisibleClassName]:
                                            isVisibleClassName !== null &&
                                            index >= currentIndex &&
                                            index < currentIndex + numberOfVisibleItems,
                                        [styles.isVisible]:
                                            index >= currentIndex &&
                                            index < currentIndex + numberOfVisibleItems,
                                    })}
                                    ref={(ref) => {
                                        itemsRef.current[index] = ref;
                                        if (index === 0) {
                                            firstItemResizeRef.current = ref;
                                        }
                                    }}
                                >
                                    <CardComponent
                                        className={classNames(styles.card, {
                                            [cardClassName]: cardClassName !== null,
                                        })}
                                        item={item}
                                        isFocusable={
                                            index >= currentIndex &&
                                            index < currentIndex + numberOfVisibleItems
                                        }
                                        isDark={isDark}
                                        isModal
                                        // selected={currentId !== null && currentId === item?.id}
                                        {...cardProps}
                                    />
                                </div>
                            ))}
                        </animated.div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

CarouselList.propTypes = propTypes;
CarouselList.defaultProps = defaultProps;

export default React.forwardRef((props, ref) => <CarouselList {...props} containerRef={ref} />);
